import { supabase, getCurrentUserId, isSupabaseConfigured } from "@/lib/supabase";
import type {
  WorkflowArtifact,
  WorkflowCheckpoint,
  WorkflowRun,
  WorkflowRunEvent,
  WorkflowRunState,
  WorkflowRunStatus,
  WorkflowVersion,
} from "@/types/workflowEngine";
import type { WorkflowEdge, WorkflowNode, WorkflowTrigger } from "@/types/aiToolkit";

function mapRun(row: Record<string, unknown>): WorkflowRun {
  return {
    id: row.id as string,
    userId: row.user_id as string,
    workflowId: row.workflow_id as string,
    workflowVersionId: (row.workflow_version_id as string) ?? null,
    status: row.status as WorkflowRunStatus,
    triggerType: (row.trigger_type as string) ?? null,
    triggerPayload: (row.trigger_payload as Record<string, unknown>) ?? {},
    currentNodeId: (row.current_node_id as string) ?? null,
    state: (row.state as WorkflowRunState) ?? { variables: {}, contextBlocks: {}, nodeOutputs: {}, entities: { people: [], dates: [] }, parallel: { activeBranches: {}, mergeBuffers: {} }, metadata: {} },
    policySnapshot: (row.policy_snapshot as Record<string, unknown>) ?? {},
    error: (row.error as string) ?? null,
    startedAt: (row.started_at as string) ?? null,
    completedAt: (row.completed_at as string) ?? null,
    createdAt: row.created_at as string,
  };
}

function mapVersion(row: Record<string, unknown>): WorkflowVersion {
  return {
    id: row.id as string,
    workflowId: row.workflow_id as string,
    userId: row.user_id as string,
    versionNumber: row.version_number as number,
    nodes: (row.nodes as WorkflowNode[]) ?? [],
    edges: (row.edges as WorkflowEdge[]) ?? [],
    entryNodeId: (row.entry_node_id as string) ?? null,
    trigger: (row.trigger as WorkflowTrigger) ?? { type: "manual", config: {} },
    variables: row.variables as unknown[] | undefined,
    changelog: (row.changelog as string) ?? null,
    publishedAt: row.published_at as string,
  };
}

class WorkflowRunsService {
  async publishVersion(
    workflowId: string,
    snapshot: {
      nodes: WorkflowNode[];
      edges: WorkflowEdge[];
      entryNodeId?: string;
      trigger: WorkflowTrigger;
      variables?: unknown[];
      changelog?: string;
    }
  ): Promise<WorkflowVersion | null> {
    if (!isSupabaseConfigured) return null;
    const userId = await getCurrentUserId();
    if (!userId) return null;

    const { data: latest } = await supabase
      .from("workflow_versions")
      .select("version_number")
      .eq("workflow_id", workflowId)
      .order("version_number", { ascending: false })
      .limit(1)
      .maybeSingle();

    const versionNumber = ((latest?.version_number as number) ?? 0) + 1;

    const { data, error } = await supabase
      .from("workflow_versions")
      .insert({
        workflow_id: workflowId,
        user_id: userId,
        version_number: versionNumber,
        nodes: snapshot.nodes,
        edges: snapshot.edges,
        entry_node_id: snapshot.entryNodeId ?? null,
        trigger: snapshot.trigger,
        variables: snapshot.variables ?? null,
        changelog: snapshot.changelog ?? null,
      })
      .select()
      .single();

    if (error || !data) {
      console.error("[workflowRuns] publishVersion:", error);
      return null;
    }

    await supabase
      .from("ai_workflows")
      .update({ published_version_id: data.id, draft_dirty: false })
      .eq("id", workflowId)
      .eq("user_id", userId);

    return mapVersion(data as Record<string, unknown>);
  }

  async getVersion(versionId: string): Promise<WorkflowVersion | null> {
    if (!isSupabaseConfigured) return null;
    const { data, error } = await supabase.from("workflow_versions").select("*").eq("id", versionId).single();
    if (error || !data) return null;
    return mapVersion(data as Record<string, unknown>);
  }

  async createRun(input: {
    workflowId: string;
    workflowVersionId?: string | null;
    triggerType?: string;
    triggerPayload?: Record<string, unknown>;
    policySnapshot?: Record<string, unknown>;
    initialState?: WorkflowRunState;
  }): Promise<WorkflowRun | null> {
    if (!isSupabaseConfigured) return null;
    const userId = await getCurrentUserId();
    if (!userId) return null;

    const { data, error } = await supabase
      .from("workflow_runs")
      .insert({
        user_id: userId,
        workflow_id: input.workflowId,
        workflow_version_id: input.workflowVersionId ?? null,
        status: "pending",
        trigger_type: input.triggerType ?? "manual",
        trigger_payload: input.triggerPayload ?? {},
        state: input.initialState ?? { variables: input.triggerPayload ?? {}, contextBlocks: {}, nodeOutputs: {}, entities: { people: [], dates: [] }, parallel: { activeBranches: {}, mergeBuffers: {} }, metadata: {} },
        policy_snapshot: input.policySnapshot ?? {},
        started_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error || !data) {
      console.error("[workflowRuns] createRun:", error);
      return null;
    }
    return mapRun(data as Record<string, unknown>);
  }

  async updateRun(
    runId: string,
    patch: Partial<{
      status: WorkflowRunStatus;
      currentNodeId: string | null;
      state: WorkflowRunState;
      error: string | null;
      completedAt: string | null;
    }>
  ): Promise<void> {
    if (!isSupabaseConfigured) return;
    const payload: Record<string, unknown> = {};
    if (patch.status !== undefined) payload.status = patch.status;
    if (patch.currentNodeId !== undefined) payload.current_node_id = patch.currentNodeId;
    if (patch.state !== undefined) payload.state = patch.state;
    if (patch.error !== undefined) payload.error = patch.error;
    if (patch.completedAt !== undefined) payload.completed_at = patch.completedAt;
    await supabase.from("workflow_runs").update(payload).eq("id", runId);
  }

  async getRun(runId: string): Promise<WorkflowRun | null> {
    if (!isSupabaseConfigured) return null;
    const userId = await getCurrentUserId();
    if (!userId) return null;
    const { data, error } = await supabase.from("workflow_runs").select("*").eq("id", runId).eq("user_id", userId).single();
    if (error || !data) return null;
    return mapRun(data as Record<string, unknown>);
  }

  async listRuns(workflowId: string, limit = 20): Promise<WorkflowRun[]> {
    if (!isSupabaseConfigured) return [];
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data } = await supabase
      .from("workflow_runs")
      .select("*")
      .eq("workflow_id", workflowId)
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(limit);
    return (data ?? []).map((r) => mapRun(r as Record<string, unknown>));
  }

  async appendEvent(event: Omit<WorkflowRunEvent, "id" | "createdAt">): Promise<WorkflowRunEvent | null> {
    if (!isSupabaseConfigured) return null;
    const { data, error } = await supabase
      .from("workflow_run_events")
      .insert({
        run_id: event.runId,
        node_id: event.nodeId,
        node_type: event.nodeType,
        event_type: event.eventType,
        input_snapshot: event.inputSnapshot ?? null,
        output_snapshot: event.outputSnapshot ?? null,
        confidence: event.confidence ?? null,
        quality: event.quality ?? null,
        validation_result: event.validationResult ?? null,
        duration_ms: event.durationMs ?? null,
        actor: event.actor,
        parallel_branch_id: event.parallelBranchId ?? null,
        metadata: event.metadata ?? {},
      })
      .select()
      .single();

    if (error || !data) {
      console.error("[workflowRuns] appendEvent:", error);
      return null;
    }
    const row = data as Record<string, unknown>;
    return {
      id: row.id as string,
      runId: row.run_id as string,
      nodeId: (row.node_id as string) ?? null,
      nodeType: (row.node_type as string) ?? null,
      eventType: row.event_type as WorkflowRunEvent["eventType"],
      inputSnapshot: row.input_snapshot,
      outputSnapshot: row.output_snapshot,
      confidence: row.confidence as number | null,
      quality: row.quality as number | null,
      validationResult: row.validation_result as WorkflowRunEvent["validationResult"],
      durationMs: row.duration_ms as number | null,
      actor: row.actor as "system" | "user",
      parallelBranchId: (row.parallel_branch_id as string) ?? null,
      metadata: (row.metadata as Record<string, unknown>) ?? {},
      createdAt: row.created_at as string,
    };
  }

  async listEvents(runId: string): Promise<WorkflowRunEvent[]> {
    if (!isSupabaseConfigured) return [];
    const { data } = await supabase
      .from("workflow_run_events")
      .select("*")
      .eq("run_id", runId)
      .order("created_at", { ascending: true });
    return (data ?? []).map((row) => {
      const r = row as Record<string, unknown>;
      return {
        id: r.id as string,
        runId: r.run_id as string,
        nodeId: (r.node_id as string) ?? null,
        nodeType: (r.node_type as string) ?? null,
        eventType: r.event_type as WorkflowRunEvent["eventType"],
        inputSnapshot: r.input_snapshot,
        outputSnapshot: r.output_snapshot,
        confidence: r.confidence as number | null,
        quality: r.quality as number | null,
        validationResult: r.validation_result as WorkflowRunEvent["validationResult"],
        durationMs: r.duration_ms as number | null,
        actor: r.actor as "system" | "user",
        parallelBranchId: (r.parallel_branch_id as string) ?? null,
        metadata: (r.metadata as Record<string, unknown>) ?? {},
        createdAt: r.created_at as string,
      };
    });
  }

  async createCheckpoint(input: {
    runId: string;
    nodeId: string;
    checkpointType: WorkflowCheckpoint["checkpointType"];
    outputOverride?: unknown;
    chatThreadId?: string;
  }): Promise<WorkflowCheckpoint | null> {
    if (!isSupabaseConfigured) return null;
    const { data, error } = await supabase
      .from("workflow_checkpoints")
      .insert({
        run_id: input.runId,
        node_id: input.nodeId,
        checkpoint_type: input.checkpointType,
        output_override: input.outputOverride ?? null,
        chat_thread_id: input.chatThreadId ?? null,
      })
      .select()
      .single();
    if (error || !data) return null;
    const row = data as Record<string, unknown>;
    return {
      id: row.id as string,
      runId: row.run_id as string,
      nodeId: row.node_id as string,
      checkpointType: row.checkpoint_type as WorkflowCheckpoint["checkpointType"],
      outputOverride: row.output_override,
      chatThreadId: (row.chat_thread_id as string) ?? null,
      resolvedAt: (row.resolved_at as string) ?? null,
      createdAt: row.created_at as string,
    };
  }

  async resolveCheckpoint(checkpointId: string, outputOverride?: unknown): Promise<void> {
    if (!isSupabaseConfigured) return;
    await supabase
      .from("workflow_checkpoints")
      .update({
        resolved_at: new Date().toISOString(),
        ...(outputOverride !== undefined ? { output_override: outputOverride } : {}),
      })
      .eq("id", checkpointId);
  }

  async getCheckpointByChatThreadId(chatThreadId: string): Promise<WorkflowCheckpoint | null> {
    if (!isSupabaseConfigured) return null;
    const { data } = await supabase
      .from("workflow_checkpoints")
      .select("*")
      .eq("chat_thread_id", chatThreadId)
      .is("resolved_at", null)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!data) return null;
    const row = data as Record<string, unknown>;
    return {
      id: row.id as string,
      runId: row.run_id as string,
      nodeId: row.node_id as string,
      checkpointType: row.checkpoint_type as WorkflowCheckpoint["checkpointType"],
      outputOverride: row.output_override,
      chatThreadId: (row.chat_thread_id as string) ?? null,
      resolvedAt: (row.resolved_at as string) ?? null,
      createdAt: row.created_at as string,
    };
  }

  async createArtifact(input: Omit<WorkflowArtifact, "id" | "createdAt">): Promise<WorkflowArtifact | null> {
    if (!isSupabaseConfigured) return null;
    const { data, error } = await supabase
      .from("workflow_artifacts")
      .insert({
        run_id: input.runId,
        node_id: input.nodeId,
        user_id: input.userId,
        artifact_type: input.artifactType,
        thread_id: input.threadId ?? null,
        kb_document_id: input.kbDocumentId ?? null,
        clickup_task_id: input.clickupTaskId ?? null,
        gmail_id: input.gmailId ?? null,
        external_id: input.externalId ?? null,
        title: input.title ?? null,
        url: input.url ?? null,
        metadata: input.metadata ?? {},
      })
      .select()
      .single();
    if (error || !data) {
      console.error("[workflowRuns] createArtifact:", error);
      return null;
    }
    const row = data as Record<string, unknown>;
    return {
      id: row.id as string,
      runId: row.run_id as string,
      nodeId: (row.node_id as string) ?? null,
      userId: row.user_id as string,
      artifactType: row.artifact_type as WorkflowArtifact["artifactType"],
      threadId: (row.thread_id as string) ?? null,
      kbDocumentId: (row.kb_document_id as string) ?? null,
      clickupTaskId: (row.clickup_task_id as string) ?? null,
      gmailId: (row.gmail_id as string) ?? null,
      externalId: (row.external_id as string) ?? null,
      title: (row.title as string) ?? null,
      url: (row.url as string) ?? null,
      metadata: (row.metadata as Record<string, unknown>) ?? {},
      createdAt: row.created_at as string,
    };
  }

  async listArtifacts(runId: string): Promise<WorkflowArtifact[]> {
    if (!isSupabaseConfigured) return [];
    const { data } = await supabase.from("workflow_artifacts").select("*").eq("run_id", runId).order("created_at", { ascending: true });
    return (data ?? []).map((row) => {
      const r = row as Record<string, unknown>;
      return {
        id: r.id as string,
        runId: r.run_id as string,
        nodeId: (r.node_id as string) ?? null,
        userId: r.user_id as string,
        artifactType: r.artifact_type as WorkflowArtifact["artifactType"],
        threadId: (r.thread_id as string) ?? null,
        kbDocumentId: (r.kb_document_id as string) ?? null,
        clickupTaskId: (r.clickup_task_id as string) ?? null,
        gmailId: (r.gmail_id as string) ?? null,
        externalId: (r.external_id as string) ?? null,
        title: (r.title as string) ?? null,
        url: (r.url as string) ?? null,
        metadata: (r.metadata as Record<string, unknown>) ?? {},
        createdAt: r.created_at as string,
      };
    });
  }
}

export const workflowRunsService = new WorkflowRunsService();
